import { useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import Button, { ButtonType, ButtonVariant } from 'src/components/atoms/Button';
import ButtonLink, { ButtonLinkVariant } from 'src/components/atoms/ButtonLink';
import Gap from 'src/components/atoms/Gap';
import { InputText, InputTextType } from 'src/components/atoms/InputText';
import { Row } from 'src/components/atoms/Row';
import Spinner from 'src/components/atoms/Spinner';
import { SpinnerColor, SpinnerSize } from 'src/components/atoms/Spinner/Spinner';
import { ControlledDropdown } from 'src/components/molecules/Dropdown';
import { STATES } from 'src/constants/states';
import useCheckOwnerNameAvailability from 'src/features/owner/hooks/useCheckOwnerNameAvailability';
import useCheckOwnerSalesforceIdAvailability from 'src/features/owner/hooks/useCheckOwnerSalesforceIdAvailability';
import { OwnerField, type IOwner as OwnerFormInputs } from 'src/features/owner/ts/interfaces/Owner';
import { getDifferences, isValidSalesforceId } from 'src/helpers';
import { useDebounce } from 'src/hooks';
import OwnerAddressInput from './OwnerAddressInput';
import formFieldsConfig from './OwnerFormFieldsConfig';
import { Container } from './styles';

export interface OwnerFormProps {
  defaultValues?: Partial<OwnerFormInputs>;
  isCreateForm?: boolean;
  isAdmin?: boolean;
  onSave?: (data: OwnerFormInputs) => void;
  handleSaveLoading?: boolean;
  onCancel?: () => void;
}

const OwnerForm: React.FC<OwnerFormProps> = (props: OwnerFormProps) => {
  const {
    defaultValues = {
      address: '',
      city: '',
      state: undefined,
      zip: '',
      name: '',
      salesforce_id: '',
      website: '',
      phone: ''
    },
    isCreateForm,
    onSave,
    handleSaveLoading
  } = props;
  const methods = useForm<OwnerFormInputs>({
    mode: 'onBlur',
    reValidateMode: 'onSubmit',
    defaultValues: defaultValues || {}
  });

  const { setError, clearErrors, watch } = methods;

  // Memoize the isDirty calculation to prevent unnecessary re-renders
  const currentValues = watch();
  const isDirty = useMemo(() => {
    const { hasDifferences } = getDifferences(defaultValues, currentValues);
    return hasDifferences;
  }, [defaultValues, currentValues]);

  const watchedFormName = watch(OwnerField.name);
  const watchedFormSalesforceId = watch(OwnerField.salesforce_id);

  /** Check owner name availability */
  const {
    checkOwnerName,
    isLoading: isOwnerNameCheckLoading,
    isAvailable: isOwnerNameAvailable
  } = useCheckOwnerNameAvailability();
  const [watchedOwnerName, setWatchedOwnerName] = useState('');
  const debouncedNameCheck = useDebounce(watchedOwnerName, 500);
  useEffect(() => {
    if (debouncedNameCheck.length) {
      checkOwnerName(debouncedNameCheck);
    }
  }, [debouncedNameCheck, checkOwnerName]);
  const [ownerNameIndicator, setOwnerNameIndicator] = useState(<></>);
  useEffect(() => {
    if (watchedFormName === defaultValues.name) {
      setOwnerNameIndicator(<></>);
      return;
    }
    if (isOwnerNameCheckLoading) {
      setOwnerNameIndicator(
        <Spinner
          size={SpinnerSize.small}
          color={SpinnerColor.primary}
          centered
          data-testid="owner-name-validation-spinner"
        />
      );
      return;
    }
    if (isOwnerNameAvailable === true) {
      setOwnerNameIndicator(
        <img
          src="/assets/images/check.svg"
          alt="Available"
          data-testid="owner-name-validation-success"
        />
      );
      clearErrors(OwnerField.name);
    } else if (isOwnerNameAvailable === false) {
      setOwnerNameIndicator(
        <img
          src="/assets/images/Failed.svg"
          alt="Not Available"
          data-testid="owner-name-validation-error"
        />
      );
      setError(OwnerField.name, { type: 'manual', message: 'Owner name already exists' });
    } else if (isOwnerNameAvailable === null) {
      setOwnerNameIndicator(<></>);
    }
  }, [
    isOwnerNameCheckLoading,
    isOwnerNameAvailable,
    watchedFormName,
    defaultValues.name,
    setError,
    clearErrors
  ]);
  /*******************************************/

  /* Check the salesforce ID availability */
  const {
    checkSalesforceId,
    isLoading: isSalesforceIdCheckLoading,
    isAvailable: isSalesforceIdAvailable
  } = useCheckOwnerSalesforceIdAvailability();
  const [watchedSalesforceId, setWatchedSalesforceId] = useState('');
  const debouncedSalesforceIdCheck = useDebounce(watchedSalesforceId, 500);
  useEffect(() => {
    if (debouncedSalesforceIdCheck.length) {
      checkSalesforceId(debouncedSalesforceIdCheck);
    }
  }, [debouncedSalesforceIdCheck, checkSalesforceId]);
  const [salesforceIdIndicator, setSalesforceIdIndicator] = useState(<></>);
  useEffect(() => {
    if (
      watchedFormSalesforceId === defaultValues.salesforce_id ||
      isValidSalesforceId(watchedFormSalesforceId) === false
    ) {
      setSalesforceIdIndicator(<></>);
      return;
    }
    if (isSalesforceIdCheckLoading) {
      setSalesforceIdIndicator(
        <Spinner
          size={SpinnerSize.small}
          color={SpinnerColor.primary}
          centered
          data-testid="owner-salesforce-validation-spinner"
        />
      );
      return;
    }
    if (isSalesforceIdAvailable === true) {
      setSalesforceIdIndicator(
        <img
          src="/assets/images/check.svg"
          alt="Available"
          data-testid="owner-salesforce-validation-success"
        />
      );
      clearErrors(OwnerField.salesforce_id);
    } else if (isSalesforceIdAvailable === false) {
      setSalesforceIdIndicator(
        <img
          src="/assets/images/Failed.svg"
          alt="Not Available"
          data-testid="owner-salesforce-validation-error"
        />
      );
      setError(OwnerField.salesforce_id, {
        type: 'manual',
        message: 'An Owner with this ID already exists'
      });
    } else {
      setSalesforceIdIndicator(<></>);
    }
  }, [
    isSalesforceIdCheckLoading,
    isSalesforceIdAvailable,
    setError,
    defaultValues.salesforce_id,
    clearErrors,
    watchedFormSalesforceId
  ]);
  /*******************************************/

  // Check if there are validation errors on critical fields
  const hasValidationIssues =
    isSalesforceIdCheckLoading ||
    isOwnerNameCheckLoading ||
    isSalesforceIdAvailable === false ||
    isOwnerNameAvailable === false;

  const handleOnSave = (data: OwnerFormInputs) => {
    // Handle form submission
    onSave?.(data);
  };

  return (
    <>
      <Gap height={2} />
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(handleOnSave)} noValidate>
          <Container>
            <Row gap={2}>
              <Row.Col size={7}>
                <Row gap={4}>
                  <Row.Col size={1}>
                    {' '}
                    <InputText
                      placeholder={formFieldsConfig.name.placeholder}
                      name={formFieldsConfig.name.name}
                      type={InputTextType.text}
                      config={{ ...formFieldsConfig.name.validationSchema }}
                      endAndorment={ownerNameIndicator}
                      onChange={({ value }) => {
                        setWatchedOwnerName(value.toString());
                      }}
                    />
                  </Row.Col>
                </Row>
              </Row.Col>
            </Row>
            <Row gap={1}>
              <Row.Col size={1}>
                <OwnerAddressInput
                  methods={methods}
                  name={OwnerField.address}
                  isCreateForm={isCreateForm}
                  defaultValues={defaultValues}
                />
              </Row.Col>
            </Row>

            <Row gap={1}>
              <Row.Col size={1}>
                <InputText
                  placeholder={formFieldsConfig.city.placeholder}
                  name={formFieldsConfig.city.name}
                  type={InputTextType.text}
                  className="optional-field"
                  config={formFieldsConfig.city.validationSchema}
                />
              </Row.Col>
              <Row.Col size={1}>
                <ControlledDropdown
                  showLabel
                  placeholder={formFieldsConfig.state.placeholder}
                  name={formFieldsConfig.state.name}
                  data={[{ label: '-- Select State --', value: 'null' }, ...STATES]}
                  config={formFieldsConfig.state.validationSchema}
                  size="md"
                />
              </Row.Col>
            </Row>
            <Row gap={1}>
              <Row.Col size={1}>
                <InputText
                  placeholder={formFieldsConfig.zip.placeholder}
                  className="optional-field"
                  name={formFieldsConfig.zip.name}
                  type={InputTextType.text}
                  config={formFieldsConfig.zip.validationSchema}
                />
              </Row.Col>
              <Row.Col size={1}>
                <InputText
                  className="optional-field"
                  placeholder={formFieldsConfig.phone.placeholder}
                  name={formFieldsConfig.phone.name}
                  type={InputTextType.tel}
                  config={formFieldsConfig.phone.validationSchema}
                />
              </Row.Col>
            </Row>
            <Row gap={1}>
              <Row.Col size={1}>
                <InputText
                  className="optional-field"
                  placeholder="Website"
                  name="website"
                  type={InputTextType.text}
                  config={formFieldsConfig.website.validationSchema}
                />
              </Row.Col>
              <Row.Col size={1}>
                <InputText
                  placeholder={formFieldsConfig.salesforce_id.placeholder}
                  name={formFieldsConfig.salesforce_id.name}
                  type={InputTextType.text}
                  config={formFieldsConfig.salesforce_id.validationSchema}
                  onChange={({ value }) => {
                    setWatchedSalesforceId(value.toString());
                  }}
                  endAndorment={salesforceIdIndicator}
                />
              </Row.Col>
            </Row>
            <Row gap={1}>
              <Button
                className="save-button"
                name="SubmitOwnerDetails"
                variant={ButtonVariant.contained}
                isDisabled={!isDirty || handleSaveLoading || hasValidationIssues}
                isLoading={handleSaveLoading}
                type={ButtonType.submit}
              >
                {handleSaveLoading ? 'Saving' : 'Save'}
              </Button>
              {Boolean(props.onCancel) && (
                <Button
                  className="cancel-button"
                  name="CancelOwnerDetails"
                  variant={ButtonVariant.outline}
                  onClick={props.onCancel}
                >
                  Cancel
                </Button>
              )}
              {!props.onCancel && (
                <ButtonLink name="Cancel" variant={ButtonLinkVariant.outline} to="/owners">
                  Cancel
                </ButtonLink>
              )}
            </Row>
          </Container>
        </form>
      </FormProvider>
    </>
  );
};

export default OwnerForm;
